import React, { useMemo, useState } from 'react';

import { Button, Card, Col, Pagination, Row, Table } from 'react-bootstrap';

import DeviatedPriceAnalysisUploadModal from 'components/rebate_analysis/DeviatedPriceAnalysisUploadModal';
import DeviatedPriceAnalysisUploadRow from 'components/rebate_analysis/DeviatedPriceAnalysisUploadRow';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import useDelete from 'lib/hooks/useDelete';
import useDpaUploadPolling from 'lib/hooks/useDpaUploadPolling';
import useGet from 'lib/hooks/useGet';
import {
  DEVIATED_PRICE_ANALYSIS_GENERATE_REPORT,
  DEVIATED_PRICE_ANALYSIS_UPLOAD,
  DEVIATED_PRICE_ANALYSIS_UPLOADS,
  getURL,
} from 'lib/networking/endpoints';
import { httpPost } from 'lib/networking/http';
import SwalDialog, { areYouSure } from 'lib/utils/toast';

const PER_PAGE = 50;

function DeviatedPriceAnalysisSection() {
  const [showPoModal, setShowPoModal] = useState(false);
  const [generatingPoId, setGeneratingPoId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    data: poUploadsData,
    loading: poLoading,
    error: poError,
    refetch: poRefetch,
    silentRefetch: poSilentRefetch,
  } = useGet(DEVIATED_PRICE_ANALYSIS_UPLOADS, { page: currentPage, per_page: PER_PAGE });
  const poUploads = useMemo(() => poUploadsData?.data || [], [poUploadsData]);
  const totalPoUploads = poUploadsData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalPoUploads / PER_PAGE));

  const { pendingGenerationIds, addPendingGeneration } = useDpaUploadPolling(
    poUploads,
    poSilentRefetch,
  );

  const { deleteData: deletePoUpload } = useDelete(
    DEVIATED_PRICE_ANALYSIS_UPLOAD,
    false,
    () => {
      poRefetch();
      const lastPage = Math.max(1, Math.ceil((totalPoUploads - 1) / PER_PAGE));
      setCurrentPage(p => Math.min(p, lastPage));
    },
    () => SwalDialog('error', 'An error occurred while deleting the PO file.', 'Error'),
  );

  // eslint-disable-next-line space-before-function-paren
  const handleGenerateReport = async upload => {
    setGeneratingPoId(upload.id);
    try {
      const url = getURL(DEVIATED_PRICE_ANALYSIS_GENERATE_REPORT, { upload_id: upload.id });
      await httpPost(url);
      SwalDialog('success', 'Report generation started.');
      addPendingGeneration(upload.id);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      SwalDialog('error', `Failed to generate report: ${msg}`, 'Error');
    } finally {
      setGeneratingPoId(null);
    }
  };

  return (
    <>
      <Row className="mt-5 mb-4">
        <Col md={5}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3 font-weight-bold">Upload Purchase Order File</Card.Title>
              <Card.Text className="text-muted">
                Upload a PO CSV file to generate a deviated pricing analysis report comparing landed
                costs against contracted prices.
              </Card.Text>
              <Button variant="primary" onClick={() => setShowPoModal(true)}>
                Upload PO File
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <h5 className="font-weight-bold ml-3 mb-3">Purchase Order Uploads</h5>
      {poLoading ? (
        <LoadingSpinner short />
      ) : poError && !poUploadsData ? (
        <p className="text-danger ml-3">Failed to load PO uploads. Please refresh the page.</p>
      ) : (
        <Table bordered striped hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>File Name</th>
              <th>Action</th>
              <th>Downloads</th>
              <th>Status</th>
              <th>Uploaded At</th>
              <th>Uploaded By</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {poUploads.length ? (
              poUploads.map(upload => (
                <DeviatedPriceAnalysisUploadRow
                  key={upload.id}
                  upload={upload}
                  onDelete={() =>
                    areYouSure(
                      () => deletePoUpload({ upload_id: upload.id }),
                      'This will permanently delete the PO upload and its report.',
                    )
                  }
                  onGenerateReport={() => handleGenerateReport(upload)}
                  isGenerating={
                    generatingPoId === upload.id || pendingGenerationIds.includes(upload.id)
                  }
                />
              ))
            ) : (
              <tr>
                <td className="text-center" colSpan="100%">
                  No PO files uploaded yet.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
      {!poLoading && !poError && totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center ml-3 mr-3 mt-2 mb-3">
          <small className="text-muted">
            {totalPoUploads.toLocaleString()} upload{totalPoUploads !== 1 ? 's' : ''} total
          </small>
          <Pagination className="mb-0" size="sm">
            <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
            <Pagination.Prev
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            />
            <Pagination.Item disabled>
              {currentPage} / {totalPages}
            </Pagination.Item>
            <Pagination.Next
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            />
            <Pagination.Last
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
            />
          </Pagination>
        </div>
      )}
      {showPoModal && (
        <DeviatedPriceAnalysisUploadModal
          onHide={() => setShowPoModal(false)}
          onSuccess={() => {
            setShowPoModal(false);
            SwalDialog('success', 'PO file uploaded successfully.');
            setCurrentPage(1);
            poRefetch();
          }}
        />
      )}
    </>
  );
}

export default DeviatedPriceAnalysisSection;
